import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, type CameraProfile, type Scope, type Store } from "../api";
import { useWords } from "../words";

// What this user is looking at: all stores, one store, or a camera profile.
//
// Scope is per-user and lives in the browser. The pipeline runs every store's
// cameras at once, so narrowing the view is a display decision, and making it
// a server-side global (as the old "active store" was) meant one operator
// switching stores silently re-scoped everyone else's screen.
//
// Every scoped request carries the scope explicitly, and every scoped query key
// includes `scopeKey`, so React Query caches store A and store B separately and
// a switch can never show A's data under B's name.

const STORAGE_KEY = "bf-scope";

export type ScopeKind = "all" | "store" | "profile";

export type ScopeValue = {
  kind: ScopeKind;
  /** Store name when kind === "store". */
  store: string;
  /** Profile id when kind === "profile". */
  profileId: string;
};

const ALL: ScopeValue = { kind: "all", store: "", profileId: "" };

type ScopeContextValue = {
  scope: ScopeValue;
  /** Params for scoped API calls. Empty object = every store. */
  scopeParams: Scope | undefined;
  /** Stable string for React Query keys. */
  scopeKey: string;
  /** Human-readable name of the current scope, for headings and empty states. */
  scopeLabel: string;
  setScope: (next: ScopeValue) => void;
  selectStore: (name: string) => void;
  selectProfile: (id: string) => void;
  selectAll: () => void;
  stores: Store[];
  profiles: CameraProfile[];
  loading: boolean;
  /** Camera ids in scope, or null when the scope is every camera. */
  scopedCameraIds: string[] | null;
};

const Ctx = createContext<ScopeContextValue | null>(null);

export function useScope(): ScopeContextValue {
  const v = useContext(Ctx);
  if (!v) throw new Error("ScopeProvider missing");
  return v;
}

function readStored(): ScopeValue {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return ALL;
    const parsed = JSON.parse(raw) as Partial<ScopeValue>;
    if (parsed.kind === "store" && parsed.store) {
      return { kind: "store", store: parsed.store, profileId: "" };
    }
    if (parsed.kind === "profile" && parsed.profileId) {
      return { kind: "profile", store: "", profileId: parsed.profileId };
    }
  } catch {
    /* private browsing / corrupt value: fall through to "all" */
  }
  return ALL;
}

export function ScopeProvider({ children }: { children: ReactNode }) {
  const qc = useQueryClient();
  const w = useWords();
  const [scope, setScopeState] = useState<ScopeValue>(readStored);

  const storesQuery = useQuery({ queryKey: ["stores"], queryFn: api.stores.get });
  const profilesQuery = useQuery({
    queryKey: ["profiles"],
    queryFn: api.profiles.list,
  });

  const stores = useMemo(() => storesQuery.data?.stores ?? [], [storesQuery.data]);
  const profiles = useMemo(() => profilesQuery.data ?? [], [profilesQuery.data]);

  const setScope = useCallback(
    (next: ScopeValue) => {
      setScopeState((prev) => {
        if (
          prev.kind === next.kind &&
          prev.store === next.store &&
          prev.profileId === next.profileId
        ) {
          return prev;
        }
        try {
          localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
        } catch {
          /* storage disabled: the scope simply resets next reload */
        }
        // Drop everything scoped so no view can paint the previous store's
        // cameras, alerts or results while its own refetch is still in flight.
        void qc.invalidateQueries();
        return next;
      });
    },
    [qc],
  );

  // A scope pointing at something that no longer exists (store deleted, or a
  // profile removed by another admin) silently falls back to "all" rather than
  // leaving every page empty with no explanation.
  useEffect(() => {
    if (storesQuery.isLoading || profilesQuery.isLoading) return;
    if (scope.kind === "store" && !stores.some((s) => s.name === scope.store)) {
      setScope(ALL);
    }
    if (
      scope.kind === "profile" &&
      !profiles.some((p) => p.id === scope.profileId)
    ) {
      setScope(ALL);
    }
  }, [
    scope,
    stores,
    profiles,
    storesQuery.isLoading,
    profilesQuery.isLoading,
    setScope,
  ]);

  const value = useMemo<ScopeContextValue>(() => {
    const scopeParams: Scope | undefined =
      scope.kind === "store"
        ? { store: scope.store }
        : scope.kind === "profile"
          ? { profileId: scope.profileId }
          : undefined;

    const profile = profiles.find((p) => p.id === scope.profileId);
    const store = stores.find((s) => s.name === scope.store);
    const scopeLabel =
      scope.kind === "store"
        ? store?.display_name || scope.store
        : scope.kind === "profile"
          ? profile?.name || w.Group
          : w.allStores;

    const scopedCameraIds =
      scope.kind === "profile"
        ? (profile?.members.map((m) => m.camera_id) ?? [])
        : null;

    return {
      scope,
      scopeParams,
      scopeKey:
        scope.kind === "all"
          ? "all"
          : scope.kind === "store"
            ? `store:${scope.store}`
            : `profile:${scope.profileId}`,
      scopeLabel,
      setScope,
      selectStore: (name: string) =>
        setScope({ kind: "store", store: name, profileId: "" }),
      selectProfile: (id: string) =>
        setScope({ kind: "profile", store: "", profileId: id }),
      selectAll: () => setScope(ALL),
      stores,
      profiles,
      loading: storesQuery.isLoading || profilesQuery.isLoading,
      scopedCameraIds,
    };
  }, [
    w,
    scope,
    stores,
    profiles,
    setScope,
    storesQuery.isLoading,
    profilesQuery.isLoading,
  ]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}
